import { z } from 'zod'

import { isoDateSchema, moneyPaisa, yearMonthSchema } from '../common'

/**
 * A30 · `GET /expenses/reality-check` (§4.15, BR §7) — the highest-value block
 * in the app, and the one thing here that must never be approximated on a
 * client.
 *
 * It answers a question the trader cannot answer from a register: *the rate you
 * assumed against the rate you are actually paying.* Divide every real running
 * cost of the month — bulk feed, rent, labour, utilities — by the month's
 * animal-days, and compare it with the daily rate the book has been costing
 * with. The gap, multiplied by a real holding period, is money that was never
 * visible.
 *
 * "You assumed Rs 400/day · actually costing Rs 532/day · under-counting by
 * Rs 132/day · on a bull held 90 days that is Rs 11,880 you could not see."
 */
export const realityCheckQuery = z.object({
  month: yearMonthSchema.optional(),
  /** The holding period the last line multiplies by. Defaults to a typical hold. */
  overDays: z.coerce.number().int().positive().max(1000).optional(),
})

export const realityCheckResponse = z.object({
  month: yearMonthSchema,
  asOf: isoDateSchema,
  /** `tenant_settings.default_daily_rate_paisa` — what the book has assumed. */
  assumedPerDayPaisa: moneyPaisa(),
  /** Real running costs ÷ animal-days. */
  actualPerDayPaisa: moneyPaisa(),
  /** `actual − assumed`. Negative means the estimate is generous, which happens. */
  gapPerDayPaisa: moneyPaisa(),
  overDays: z.number().int().positive(),
  gapOverDaysPaisa: moneyPaisa(),
  totalRunningCostsPaisa: moneyPaisa(),
  animalDays: z.number().int().nonnegative(),
  /** Whether there is enough of a month recorded for the figure to mean anything. */
  hasEnoughData: z.boolean(),
})

export type RealityCheckQuery = z.input<typeof realityCheckQuery>
export type RealityCheckResponse = z.infer<typeof realityCheckResponse>
